import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';

// AIDEV-NOTE: The nav tree's own data. The lessons API's default serializer also renders every
// program a lesson is shared with (seconds per program); `only` trims it to the four fields the
// tree shows and brings it to ~150ms. Keyed apart from the SPA lessons page's full query so the
// two never serve each other's shape.

export interface TreeLesson {
  id: number;
  title: string;
  title_number: string | null;
  unit_id: number | null;
}

export interface TreeUnit {
  display_number: string;
  id: number;
  lessons: TreeLesson[];
  title: string;
}

interface RawUnit {
  display_number: string;
  id: number;
  title: string;
}

const useTreeUnits = (programId: number) => useQuery({
  queryFn: async () => {
    const { data } = await axios.get(Routes.plato_api_program_units_path(programId, { all_units: true }));
    return (data.data || []) as RawUnit[];
  },
  queryKey: [QueryKeys.ProgramUnits, programId],
});

const useTreeLessons = (programId: number) => useQuery({
  queryFn: async () => {
    const { data } = await axios.get(
      Routes.plato_api_program_lessons_path(programId, { only: ['id', 'title', 'title_number', 'unit_id'] }),
    );
    return (data.data || []) as TreeLesson[];
  },
  queryKey: [QueryKeys.Lessons, programId, 'tree'],
});

export interface ProgramTreeData {
  allLessons: TreeLesson[];
  ghostLessons: TreeLesson[];
  hasUnits: boolean;
  isLoading: boolean;
  units: TreeUnit[];
}

const useProgramTree = (programId: number): ProgramTreeData => {
  const { data: rawUnits = [], isLoading: loadingUnits } = useTreeUnits(programId);
  const { data: lessons = [], isLoading: loadingLessons } = useTreeLessons(programId);

  const { units, ghostLessons, hasUnits } = useMemo(() => {
    if (rawUnits.length === 0) return { ghostLessons: [], hasUnits: false, units: [] };

    const byUnit = new Map<number | null, TreeLesson[]>();
    lessons.forEach((lesson) => {
      byUnit.set(lesson.unit_id, [...(byUnit.get(lesson.unit_id) || []), lesson]);
    });

    return {
      ghostLessons: byUnit.get(null) || [],
      hasUnits: true,
      units: rawUnits.map(unit => ({ ...unit, lessons: byUnit.get(unit.id) || [] })),
    };
  }, [rawUnits, lessons]);

  return { allLessons: lessons, ghostLessons, hasUnits, isLoading: loadingUnits || loadingLessons, units };
};

export default useProgramTree;
